import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Search, Download, AlertTriangle, GitCompare } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadCsv, getList } from "@/lib/api";
import Modal, { inputCls, btnPrimary, btnSecondary, Field } from "@/components/Modal";
import StatusBadge from "@/components/StatusBadge";
import AllergenChips from "@/components/AllergenChips";
import Pager from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";
import { FORMULA_CATEGORIES, FORMULA_STATUSES, PAGE_SIZE, fmtDate, label } from "@/lib/constants";

export function FormulaForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial
      ? { name: initial.name, category: initial.category, status: initial.status, description: initial.description || "", serving_size_g: initial.serving_size_g ?? 20, change_note: "" }
      : { name: "", category: "bars", status: "prototype", description: "", serving_size_g: 20, change_note: "" }
  );
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/formulas/${initial.id}`, form);
        toast.success("Formula updated — new version snapshot saved");
      } else {
        await api.post("/formulas", form);
        toast.success("Formula created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial?.id ? "Edit Formula" : "New Formula"} onClose={onClose} testid="formula-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="formula-form">
        <Field label="Name">
          <input data-testid="formula-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
        </Field>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Category">
            <select data-testid="formula-category-select" value={form.category} onChange={set("category")} className={inputCls}>
              {FORMULA_CATEGORIES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select data-testid="formula-status-select" value={form.status} onChange={set("status")} className={inputCls}>
              {FORMULA_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </Field>
          <Field label="Serving (g)">
            <input data-testid="formula-serving-input" type="number" step="0.5" min={1} max={1000} value={form.serving_size_g} onChange={set("serving_size_g")} className={inputCls} />
          </Field>
        </div>
        <Field label="Description">
          <textarea data-testid="formula-description-input" rows={3} value={form.description} onChange={set("description")} className={inputCls} />
        </Field>
        {initial?.id && (
          <Field label="Change note (saved with this version)">
            <input data-testid="formula-change-note-input" value={form.change_note} onChange={set("change_note")} className={inputCls} placeholder="e.g. Raised cocoa butter for enrobing fluidity" />
          </Field>
        )}
        {!initial?.id && (
          <p className="text-xs text-[#8C7A6B]" data-testid="version-hint">
            Versions are assigned automatically — every edit writes an immutable snapshot you can diff and restore.
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="formula-cancel-button">Cancel</button>
          <button type="submit" disabled={saving} className={btnPrimary} data-testid="formula-save-button">
            {saving ? "Saving…" : "Save formula"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function Formulas() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [filters, setFilters] = useState({ category: "", status: "", q: "" });
  const [includeArchived, setIncludeArchived] = useState(false);
  const [offset, setOffset] = useState(0);
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["formulas", filters, includeArchived, offset],
    queryFn: () => getList("/formulas", { ...filters, include_archived: includeArchived || undefined, limit: PAGE_SIZE, offset }),
  });

  const formulas = data?.items ?? [];
  const setFilter = (k) => (e) => { setOffset(0); setFilters({ ...filters, [k]: e.target.value }); };

  const exportCsv = async () => {
    try {
      await downloadCsv("/formulas/export.csv", "smoor-formulas.csv");
      toast.success("Formulas exported");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="formulas-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-4 border-b border-[#E8DFD1]">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C100B]">Formulas</h1>
          <p className="text-sm text-[#8C7A6B] mt-1">Recipe engineering master list.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/formulas/compare" className={btnSecondary} data-testid="compare-formulas-button">
            <GitCompare size={15} className="inline mr-1.5 -mt-0.5" /> Compare
          </Link>
          <button onClick={exportCsv} className={btnSecondary} data-testid="export-formulas-button">
            <Download size={15} className="inline mr-1.5 -mt-0.5" /> Export CSV
          </button>
          {canWrite && (
            <button onClick={() => setShowForm(true)} className={btnPrimary} data-testid="new-formula-button">
              <Plus size={16} /> New Formula
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6 items-center" data-testid="formula-filters">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C7A6B]" />
          <input
            data-testid="formula-search-input"
            placeholder="Search formulas…"
            value={filters.q}
            onChange={setFilter("q")}
            className={`${inputCls} pl-9 w-56`}
          />
        </div>
        <select data-testid="formula-category-filter" value={filters.category} onChange={setFilter("category")} className={`${inputCls} w-48`}>
          <option value="">All categories</option>
          {FORMULA_CATEGORIES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
        </select>
        <select data-testid="formula-status-filter" value={filters.status} onChange={setFilter("status")} className={`${inputCls} w-44`}>
          <option value="">All statuses</option>
          {FORMULA_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs font-semibold text-[#5C4A3E] cursor-pointer" data-testid="include-archived-toggle">
          <input type="checkbox" checked={includeArchived} onChange={(e) => { setOffset(0); setIncludeArchived(e.target.checked); }} className="accent-[#C5A059]" data-testid="include-archived-checkbox" />
          Include archived
        </label>
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-[#E8DFD1] bg-white shadow-sm" data-testid="formulas-table">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#FAF6F0] border-b border-[#E8DFD1] text-[#2A1810] text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3.5 font-semibold">Name</th>
              <th className="px-4 py-3.5 font-semibold">Category</th>
              <th className="px-4 py-3.5 font-semibold">Status</th>
              <th className="px-4 py-3.5 font-semibold">Allergens</th>
              <th className="px-4 py-3.5 font-semibold">Version</th>
              <th className="px-4 py-3.5 font-semibold">Updated</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="formulas-loading">Loading…</td></tr>
            )}
            {!isLoading && formulas.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="formulas-empty">No formulas match these filters.</td></tr>
            )}
            {formulas.map((f) => (
              <tr
                key={f.id}
                data-testid={`formula-row-${f.id}`}
                onClick={() => navigate(`/formulas/${f.id}`)}
                className="border-b border-[#F4EFEA] hover:bg-[#FDFBF7] cursor-pointer transition-colors"
              >
                <td className="px-4 py-3.5 font-medium text-[#2A1810]">
                  {f.name}
                  {f.archived && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-[#F3F4F6] text-[#374151] border border-[#E5E7EB] px-1.5 py-0.5 rounded" data-testid={`archived-badge-${f.id}`}>Archived</span>}
                  {f.composition_ok === false && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] px-1.5 py-0.5 rounded" data-testid={`composition-warning-${f.id}`}>
                      <AlertTriangle size={10} /> {f.percentage_sum ?? 0}%
                    </span>
                  )}
                </td>
                <td className="px-4 py-3.5 text-[#5C4A3E]">{label(f.category)}</td>
                <td className="px-4 py-3.5"><StatusBadge value={f.status} /></td>
                <td className="px-4 py-3.5"><AllergenChips allergens={f.allergens} testid={`formula-allergens-${f.id}`} empty="none" /></td>
                <td className="px-4 py-3.5 text-[#5C4A3E]">v{f.version}</td>
                <td className="px-4 py-3.5 text-[#8C7A6B]">{fmtDate(f.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager offset={offset} limit={PAGE_SIZE} total={data?.total ?? 0} onChange={setOffset} testid="formulas-pager" />
      </div>

      {showForm && <FormulaForm onClose={() => setShowForm(false)} onSaved={refetch} />}
    </div>
  );
}
